import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { BarChart3, PieChart as PieChartIcon, Loader } from 'lucide-react';
import api from '../services/api';

const ChartContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const ChartHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #f8f9fa;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ChartTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ChartContent = styled.div`
  padding: 1.5rem;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const ChartGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const RevenueChart = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const BarContainer = styled.div`
  display: flex;
  align-items: end;
  justify-content: space-around;
  height: 80%;
  padding: 0 1rem;
`;

const Bar = styled.div<{ $height: number; $color: string; $value: number }>`
  width: 30px;
  height: ${props => props.$height}%;
  background: ${props => props.$color};
  border-radius: 4px 4px 0 0;
  position: relative;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }

  &::after {
    content: 'R$ ${props => props.$value}';
    position: absolute;
    top: -25px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 0.7rem;
    color: #666;
    font-weight: 500;
    white-space: nowrap;
  }
`;

const ChartLabels = styled.div`
  display: flex;
  justify-content: space-around;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  color: #666;
`;

const PieContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  position: relative;
  flex-direction: column;
  gap: 1.5rem;
`;

const PieChart = styled.div<{ $segments: Array<{ percentage: number; color: string }> }>`
  width: 180px;
  height: 180px;
  border-radius: 50%;
  background: ${props => {
    const gradientStops: string[] = [];
    let currentAngle = 0;

    props.$segments.forEach(segment => {
      const endAngle = currentAngle + (segment.percentage * 3.6);
      gradientStops.push(`${segment.color} ${currentAngle}deg ${endAngle}deg`);
      currentAngle = endAngle;
    });

    return `conic-gradient(${gradientStops.join(', ')})`;
  }};
  position: relative;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100px;
    height: 100px;
    background: white;
    border-radius: 50%;
  }
`;

const PieLegend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.875rem;
`;

const LegendLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LegendColor = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background: ${props => props.$color};
`;

const LegendValue = styled.span`
  font-weight: 600;
  color: #333;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const EmptyState = styled.div`
  text-align: center;
  color: #999;
  font-size: 0.875rem;
`;

interface DashboardChartsProps {}

export const DashboardCharts: React.FC<DashboardChartsProps> = () => {
  const [revenueData, setRevenueData] = useState<any[]>([]);
  const [planDistribution, setPlanDistribution] = useState<any[]>([]);
  const [isLoadingRevenue, setIsLoadingRevenue] = useState(true);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);

  useEffect(() => {
    loadChartData();
  }, []);

  const loadChartData = async () => {
    try {
      // Buscar receita mensal
      setIsLoadingRevenue(true);
      const revenueResponse = await api.getAdminDashboardMonthlyRevenue();
      if (revenueResponse.success) {
        // Pegar apenas os últimos 9 meses para melhor visualização
        const last9Months = revenueResponse.data.slice(-9);
        setRevenueData(last9Months);
      }
      setIsLoadingRevenue(false);

      // Buscar distribuição de planos
      setIsLoadingPlans(true);
      const plansResponse = await api.getAdminDashboardPlanDistribution();
      if (plansResponse.success) {
        setPlanDistribution(plansResponse.data);
      }
      setIsLoadingPlans(false);

    } catch (error) {
      console.error('Error loading chart data:', error);
      setIsLoadingRevenue(false);
      setIsLoadingPlans(false);
    }
  };

  const getMaxRevenue = () => {
    if (revenueData.length === 0) return 1000;
    const max = Math.max(...revenueData.map(d => d.value));
    return max > 0 ? max : 1000;
  };

  return (
    <ChartGrid>
      <ChartContainer>
        <ChartHeader>
          <ChartTitle>
            <BarChart3 size={20} />
            Receita Mensal (Últimos 9 Meses)
          </ChartTitle>
        </ChartHeader>
        <ChartContent>
          {isLoadingRevenue ? (
            <LoadingContainer>
              <Loader size={32} className="animate-spin" style={{ color: '#00d4aa' }} />
            </LoadingContainer>
          ) : revenueData.length > 0 ? (
            <RevenueChart>
              <BarContainer>
                {revenueData.map((item, index) => {
                  const maxValue = getMaxRevenue();
                  const heightPercentage = (item.value / maxValue) * 100;

                  return (
                    <Bar
                      key={index}
                      $height={Math.max(heightPercentage, 5)} // Mínimo 5% para visualização
                      $value={item.value}
                      $color="linear-gradient(135deg, #00d4aa, #00b894)"
                    />
                  );
                })}
              </BarContainer>
              <ChartLabels>
                {revenueData.map((item, index) => (
                  <span key={index}>{item.month}</span>
                ))}
              </ChartLabels>
            </RevenueChart>
          ) : (
            <EmptyState>Nenhum dado de receita disponível</EmptyState>
          )}
        </ChartContent>
      </ChartContainer>

      <ChartContainer>
        <ChartHeader>
          <ChartTitle>
            <PieChartIcon size={20} />
            Distribuição de Planos
          </ChartTitle>
        </ChartHeader>
        <ChartContent>
          {isLoadingPlans ? (
            <LoadingContainer>
              <Loader size={32} className="animate-spin" style={{ color: '#00d4aa' }} />
            </LoadingContainer>
          ) : planDistribution.length > 0 && planDistribution.some(p => p.count > 0) ? (
            <PieContainer>
              <PieChart
                $segments={planDistribution.map(plan => ({
                  percentage: plan.percentage,
                  color: plan.color
                }))}
              />
              <PieLegend>
                {planDistribution.map((plan, index) => (
                  <LegendItem key={index}>
                    <LegendLeft>
                      <LegendColor $color={plan.color} />
                      <span>{plan.name}</span>
                    </LegendLeft>
                    <LegendValue>{plan.percentage}% ({plan.count})</LegendValue>
                  </LegendItem>
                ))}
              </PieLegend>
            </PieContainer>
          ) : (
            <EmptyState>Nenhuma empresa cadastrada ainda</EmptyState>
          )}
        </ChartContent>
      </ChartContainer>
    </ChartGrid>
  );
};
